import React, { useCallback, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  RadioGroup,
  Radio,
  Stack,
  Textarea,
  Box,
  Divider,
} from "@chakra-ui/react";
import { PROPERTY_STATUS } from "constants/dbEnums";
import { UPDATE_USER_AND_PROPERTY_API } from "constants/endpoints";
import useApi from "hooks/useApi";
import { showToast } from "components/toast/Toast";

const REJECTION_REASON = [
  {
    id: 0,
    label: "Images contain watermarks from other websites",
    value: "image_contain_other_watermark",
  },
  {
    id: 1,
    label: "The listed price is incorrect or unrealistic",
    value: "incorrect_or_unrealistic_price",
  },
  {
    id: 2,
    label: "This property is already listed (duplicate ad)",
    value: "duplicate_ad",
  },
  {
    id: 3,
    label: "You’ve reached the free listing limit",
    value: "free_listing_limit_reached",
  },
  {
    id: 4,
    label: "Some images contain phone numbers (not allowed)",
    value: "images_contain_phone_numbers",
  },
  {
    id: 5,
    label: "We were unable to reach you for property verification",
    value: "unable_to_reach_for_verification",
  },
  {
    id: 6,
    label: "Other",
    value: "other",
  },
];

const REJECTION_MESSAGES = {
  image_contain_other_watermark:
    "Images with watermarks are not allowed. Please upload images without watermarks.",
  incorrect_or_unrealistic_price:
    "The price you entered seems incorrect or unrealistic. Please update it to reflect the actual property value.",
  duplicate_ad:
    "This property is already listed on our platform. Duplicate listings are not allowed.",
  free_listing_limit_reached:
    "You've reached your free listing limit. Please upgrade your plan to add more properties.",
  images_contain_phone_numbers:
    "Images can't contain phone number or any other contact information.",
  unable_to_reach_for_verification:
    "We were unable to reach you for property verification. Please connect to our support team.",
};

export default function RejectPropertyModal({ isOpen, onClose, data }) {
  const { apiCall, loading = true, error } = useApi();
  const [selectedReason, setSelectedReason] = useState("");
  const [message, setMessage] = useState("");

  const handleReasonChange = (value) => {
    setSelectedReason(value);
    const mess = REJECTION_MESSAGES[value];
    setMessage(mess || "");
  };

  const handleSubmit = async () => {
    try {
      if (!message?.trimEnd()) {
        return showToast({
          message: "Please provide message!",
          customStyle: {
            background: "red",
            color: "#fff",
            padding: "10px",
          },
        });
      }
      const { _id, collection_type } = data || {};
      const updateData = [
        {
          type: collection_type,
          _id,
          property_status: "rejected",
          notification_message: message,
        },
      ];
      await apiCall.patch(UPDATE_USER_AND_PROPERTY_API, { updateData });
      handleCloseModal(true);
    } catch (error) {
      handleCloseModal();
      showToast({
        message:
          error.response.data.error.message ||
          error.response.data.error ||
          error.message,
        customStyle: {
          background: "red",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  };

  const handleCloseModal = useCallback((updateList) => {
    setMessage("");
    setSelectedReason("");
    onClose(updateList);
  }, []);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="2xl" shadow="lg">
          <ModalHeader>Reason For Rejecting Property:</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Box>
              <RadioGroup onChange={handleReasonChange} value={selectedReason}>
                <Stack direction="column" spacing={3}>
                  {REJECTION_REASON.map(({ label, id, value }) => (
                    <Radio key={id} value={value}>
                      {label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>

              <Divider my={4} />

              <Textarea
                placeholder="Rejection message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                isDisabled={!selectedReason}
                resize="vertical"
              />
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={!selectedReason}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
